import { useMemo, useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast, type RoleState } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useStreamView } from "../../state/stream-view.tsx";
import { WriteButton, ReadOnlyNote } from "./WriteControls.tsx";
import { SourcesList } from "./SourcesList.tsx";
import { EnrolWizard } from "./EnrolWizard.tsx";
import { useSources } from "./use-sources.ts";
import {
  readErrorMessage,
  resolveLiveness,
  sourceLevel,
  sourceStatusLabel,
  livenessLabel,
  formatSeq,
  formatCount,
  formatSkew,
  type Source,
  type SourceLivenessState,
} from "./vocab.ts";
import {
  DashboardGrid,
  KpiCard,
  StatusGrid,
  type StatusGridItem,
} from "../../components/observability/index.ts";
import "./sources.css";

/**
 * The Sources screen (task 11.14 / T9.14; design 11 operator-API Sources row, 12.2 Sources row,
 * design 13 source management). An ADMIN write screen: reads need `viewer`, every write (enrol,
 * register key, revoke, approve audit key) needs `admin`, and the server enforces regardless.
 *
 * Composition:
 * - The ENROL WIZARD (admin): the four-step manual flow from design 13 — create the source, register
 *   the component's PUBLIC telemetry JWK (never a private key), pin the audit signing key (or skip
 *   for a tofu demo source), then show the copy-able component config.
 * - The REGISTERED SOURCES list: `GET /sources` for the registry, keys and pins; the live stream's
 *   per-source liveness overlaid for status FRESHNESS. Each row expands to its telemetry keys
 *   (register / revoke) and audit-key pins (approve → retroactive re-verification).
 *
 * The sources read drives the scaffold's top-level state (loading / error / empty / restricted /
 * ready). The screen respects the shared time-range pause: the registry read stops polling when
 * paused (the fast liveness changes still arrive on the stream, which the live toggle also pauses).
 *
 * CONTRACT GAP (surfaced as an honest unknown): design 12.2 asks the Sources row to surface the
 * `accepted_during_revocation_propagation` flag "where present". That boolean lives on the audit
 * CHAIN row (`AuditChainRow.accepted_during_revocation_propagation`), not on `Source` — the sources
 * registry has no such field. So this screen EXPLAINS the flag at revoke time (the revoke dialog
 * tells the admin a batch reaching an unaware edge is still processed and marked with it) and points
 * to the integrity/audit view where the per-chain flag is actually reported, rather than inventing a
 * field the `Source` schema does not define.
 */
export function SourcesScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const canRead = roleAtLeast(role, "viewer");
  const query = useSources({ enabled: canRead, live });

  // Live per-source liveness (live/silent) from the stream, overlaid on the REST snapshot for status
  // freshness. The stream only ever knows live/silent; never_seen comes from REST.
  const { view } = useStreamView();
  const streamLiveness = useMemo<ReadonlyMap<string, "live" | "silent">>(() => {
    const map = new Map<string, "live" | "silent">();
    for (const [id, s] of view.sources) map.set(id, s.state);
    return map;
  }, [view.sources]);

  const sources = useMemo<readonly Source[]>(() => query.data?.items ?? [], [query.data]);

  const state = resolveState(role, query, sources.length);

  return (
    <ScreenScaffold
      title="Sources"
      subtitle="Registered sources with status, last sequence, drops, clock skew and audit-key pins, plus the admin enroll wizard. Reading needs the viewer role; enrolling, registering keys, revoking and approving audit keys need the admin role. The server enforces regardless. The control plane never generates or holds a private key; the wizard registers only public keys."
      state={state}
    >
      <SourcesBody role={role} live={live} sources={sources} streamLiveness={streamLiveness} />
    </ScreenScaffold>
  );
}

/** Decide the scaffold state from the role and the sources query. */
function resolveState(
  role: RoleState,
  query: ReturnType<typeof useSources>,
  count: number,
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the sources view is hidden until it is. The server enforces access regardless."
          : "The sources view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    // Even with zero sources we render the ready body so the admin can reach the enrol wizard; the
    // empty case is handled by the list's own empty message. Only a non-admin with zero sources sees
    // the scaffold empty state (they cannot enrol one).
    if (count === 0 && !roleAtLeast(role, "admin")) {
      return {
        kind: "empty",
        cause:
          "No sources are registered for your team. Enrolling a source needs the admin role; ask an admin to enroll one.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: readErrorMessage(query.error) };
  }

  return { kind: "loading" };
}

function SourcesBody(props: {
  role: RoleState;
  live: boolean;
  sources: readonly Source[];
  streamLiveness: ReadonlyMap<string, "live" | "silent">;
}): ReactNode {
  const [enrolling, setEnrolling] = useState(false);

  return (
    <div className="src" data-live={props.live}>
      <section className="src-region" aria-label="Enroll a source">
        <div className="src-region__head">
          <div>
            <h2 className="src-region__title">Enroll a source</h2>
            <p className="src-region__note src-faint">
              Register a source and its public keys manually. The telemetry key pair is generated in the
              component’s environment. Only the public JWK is registered here. The control plane never
              generates or sees a private key.
            </p>
          </div>
          <WriteButton role={props.role} onClick={() => setEnrolling(true)} variant="primary">
            Enroll a source
          </WriteButton>
        </div>
        <ReadOnlyNote role={props.role} />
      </section>

      <section className="src-region" aria-label="Registered sources">
        <h2 className="src-region__title">Registered sources</h2>
        <div className="screen-meta-row">
          <LivePauseControl />
          <TimeRangeControl />
          <p className="src-region__note src-faint">
            {props.live ? "Live" : "Paused"} · status prefers the live stream’s liveness; the registry,
            keys and pins come from the sources API. A number drills down to its detail.
          </p>
        </div>

        {/* At-a-glance dashboard built from the SAME registry read (and the stream liveness overlay)
            the detailed table below uses — a KpiCard summary row and a StatusGrid of the sources,
            each cell drilling to that source's events. The full, expandable keys/pins table stays
            below; this is a presentational overview, not a new read (observability-ui task 3.1). */}
        <DashboardGrid
          label="Sources summary"
          className="src-dashboard"
          kpis={sourceKpis(props.sources, props.streamLiveness).map((kpi) => (
            <KpiCard key={kpi.key} {...kpi.props} />
          ))}
        >
          <StatusGrid
            title="Source status"
            items={sourceStatusItems(props.sources, props.streamLiveness)}
            emptyMessage="No sources are registered yet. Enroll one with the wizard above."
          />
        </DashboardGrid>

        <SourcesList role={props.role} sources={props.sources} streamLiveness={props.streamLiveness} />
      </section>

      {enrolling && <EnrolWizard role={props.role} onClose={() => setEnrolling(false)} />}
    </div>
  );
}

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: Parameters<typeof KpiCard>[0];
}

/**
 * The headline source counts as KpiCards (observability-ui task 3.1). Every value is derived from
 * the SAME registry read and stream-liveness overlay the table below uses — nothing new is fetched
 * and nothing is fabricated. "Registered" is the whole registry; "Live" the sources the stream (or
 * REST) reports as live; "Silent" those that have gone quiet (surfaced as attention-needed, never
 * green); "Revoked" the revoked ones (likewise). A clean board stays neutral.
 */
function sourceKpis(
  sources: readonly Source[],
  streamLiveness: ReadonlyMap<string, "live" | "silent">,
): readonly KeyedKpi[] {
  let live = 0;
  let silent = 0;
  let revoked = 0;
  for (const s of sources) {
    if (s.status === "revoked") {
      revoked += 1;
      continue;
    }
    const liveness = resolveLiveness(s.liveness, streamLiveness.get(s.id));
    if (liveness === "live") live += 1;
    else if (liveness === "silent") silent += 1;
  }

  return [
    { key: "registered", props: { label: "Registered", value: sources.length } },
    { key: "live", props: { label: "Live", value: live } },
    {
      key: "silent",
      props: {
        label: "Silent",
        value: silent,
        ...(silent > 0 ? { delta: `${silent}`, trend: "down" as const } : {}),
      },
    },
    {
      key: "revoked",
      props: {
        label: "Revoked",
        value: revoked,
        ...(revoked > 0 ? { delta: `${revoked}`, trend: "down" as const } : {}),
      },
    },
  ];
}

/**
 * Map the registry (with the live-stream liveness overlaid) onto the kit StatusGrid's cells. The
 * status and its level reuse the screen's existing vocabulary ({@link sourceLevel} /
 * {@link sourceStatusLabel} / {@link livenessLabel}) so a cell reads exactly as the detailed table's
 * badge does — a live source is neutral, a silent one warns, a revoked one is an error, never green
 * where not confirmed (design 12.3). Each cell drills to that source's events — the same `?source=`
 * query the detailed table's number cells use. Attention-needed sources (revoked, then silent/
 * never-seen) sort first, matching the table.
 */
function sourceStatusItems(
  sources: readonly Source[],
  streamLiveness: ReadonlyMap<string, "live" | "silent">,
): readonly StatusGridItem[] {
  const sorted = [...sources].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  return sorted.map((s): StatusGridItem => {
    const liveness = resolveLiveness(s.liveness, streamLiveness.get(s.id));
    return {
      id: s.id,
      name: s.name,
      status: sourceLevel(s, liveness),
      statusLabel: statusText(s.status, liveness),
      metrics: [
        { label: "Service", value: s.service },
        { label: "Last seq", value: formatSeq(s.last_seq) },
        { label: "Drops", value: formatCount(s.reported_drops) },
        { label: "Skew", value: formatSkew(s.clock_skew_ms) },
      ],
      href: `/events?source=${encodeURIComponent(s.id)}`,
    };
  });
}

/** The combined status + liveness text for a cell badge (mirrors the detailed table's `statusText`). */
function statusText(status: Source["status"], liveness: SourceLivenessState): string {
  if (status !== "active") return sourceStatusLabel(status);
  return livenessLabel(liveness);
}

/** Sort rank: revoked first (needs attention), then silent/never-seen, then live/active. */
function rank(s: Source): number {
  if (s.status === "revoked") return 0;
  if (s.liveness === "silent") return 1;
  if (s.liveness === "never_seen") return 2;
  return 3;
}
